import { version as PACKAGE_VERSION } from "../package.json";
import { AnalyticsService, CheckoutTimingTracker } from "./analytics";
import { connectToTunnelXIframe } from "./penpal/connect-tunnel-x";
import type { ExternalFuncsMapByPm, PaymentKit as PaymentKitType } from "./types";
import { getUrlsForEnvironment } from "./types";
import { createCheckoutIFrame } from "./utils";
import { getOrCreateCheckoutRequestId } from "./utils/checkout-request-id";

/** Append script to DOM, falling back to head if body not ready */
function appendScriptToDOM(script: HTMLScriptElement): void {
  const target = document.body ?? document.head;
  target.appendChild(script);
}

/**
 * Map fraud script URL to the script ID that the processor's script expects.
 * Airwallex's fingerprint script looks for `id="airwallex-fraud-api"` via getElementById.
 * Other processors may have different ID requirements.
 */
function getFraudScriptId(url: string): string {
  // Airwallex expects exactly "airwallex-fraud-api" as the script ID
  if (url.includes("airwallex.com")) {
    return "airwallex-fraud-api";
  }
  // Fallback for other processors: use a hash-based ID
  const urlObj = new URL(url);
  const hash = btoa(urlObj.origin + urlObj.pathname)
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, 12);
  return `pk-fraud-script-${hash}`;
}

type PaymentKitReturnType = ReturnType<PaymentKitType>;

const createTunnelXConnection = (baseUrl: string, apiBaseUrl: string, token: string) => {
  const iframe = createCheckoutIFrame("tunnel-x", baseUrl, {
    checkout_token: token,
    api_base_url: apiBaseUrl,
  });
  // Tunnel-X is a hidden communication iframe, not a visible UI element
  Object.assign(iframe.style, { width: "0", height: "0", position: "absolute", visibility: "hidden" });
  document.body.appendChild(iframe);

  const connection = connectToTunnelXIframe(iframe, {});

  let destroyed = false;
  const unmount = () => {
    if (destroyed) return;
    destroyed = true;
    connection.destroy();
    iframe.remove();
  };

  return { unmount, connection };
};

const PaymentKit: PaymentKitType = ({
  environment = "production",
  secureToken,
  paymentMethods,
  cardTokenizationMode,
  vgsVaultId,
  vgsEnvironment,
  __enableAnalyticsTestMode,
}) => {
  type PaymentMethods = typeof paymentMethods;

  // Resolve URLs from environment
  const { baseUrl, apiBaseUrl } = getUrlsForEnvironment(environment);

  // Log version for debugging
  console.log(`[PaymentKit] v${PACKAGE_VERSION} initialized (env: ${environment})`);

  // Generate checkout request ID for correlating all API calls and analytics events
  // This is idempotent - same ID is reused across multiple PaymentKit instantiations
  const checkoutRequestId = getOrCreateCheckoutRequestId(environment);
  console.log(`[PaymentKit] checkout_request_id: ${checkoutRequestId}`);

  // Initialize analytics and identify with secure token for correlation
  AnalyticsService.init(apiBaseUrl, __enableAnalyticsTestMode);
  AnalyticsService.identify(secureToken);

  // Create timing tracker for this checkout session
  const timingTracker = new CheckoutTimingTracker(secureToken, checkoutRequestId);

  // Track page ready immediately
  timingTracker.trackPageReady();

  const { connection: tunnelXConnection, unmount: unmountTunnelX } = createTunnelXConnection(
    baseUrl,
    apiBaseUrl,
    secureToken,
  );

  const paymentKitStates = {
    baseUrl,
    apiBaseUrl,
    secureToken,
    environment,
    tunnelXConnection,
    timingTracker,
    checkoutRequestId,
    cardTokenizationMode,
    vgsVaultId,
    vgsEnvironment,
    _sessionConfigReady: undefined as Promise<void> | undefined,
    _fraudScriptIds: [] as string[],
    _fraudScriptsReady: undefined as Promise<void> | undefined,
  };

  // Auto-detect VGS mode from session API if not explicitly provided.
  // The promise resolves before any card mount or submit needs the mode.
  if (cardTokenizationMode === undefined) {
    const sessionConfigUrl = `${apiBaseUrl}/api/checkout-sessions/token/${secureToken}`;
    console.log(
      `[PaymentKit] cardTokenizationMode not provided — auto-detecting from session API: ${sessionConfigUrl}`,
    );
    paymentKitStates._sessionConfigReady = fetch(sessionConfigUrl)
      .then((resp) => {
        if (!resp.ok) {
          console.warn(`[PaymentKit] Session config fetch failed with status ${resp.status}`);
          return null;
        }
        return resp.json();
      })
      .then((session) => {
        if (session && typeof session === "object" && typeof session.card_tokenization_mode === "string") {
          paymentKitStates.cardTokenizationMode = session.card_tokenization_mode || "direct";
          console.log(
            `[PaymentKit] Session config detected — cardTokenizationMode: ${paymentKitStates.cardTokenizationMode}`,
          );

          // Validate VGS config from API before trusting it
          const vaultId = session.vgs_vault_id;
          const vgsEnv = session.vgs_environment;
          if (vaultId && /^tnt[a-z0-9]+$/.test(vaultId) && (vgsEnv === "sandbox" || vgsEnv === "live")) {
            paymentKitStates.vgsVaultId = vaultId;
            paymentKitStates.vgsEnvironment = vgsEnv;
            console.log(`[PaymentKit] VGS config valid — vaultId: ${vaultId}, environment: ${vgsEnv}`);
          } else if (paymentKitStates.cardTokenizationMode === "vgs") {
            console.warn(
              `[PaymentKit] Invalid VGS config from session API (vaultId: ${vaultId}, env: ${vgsEnv}), falling back to direct mode`,
            );
            paymentKitStates.cardTokenizationMode = "direct";
          }

          // Inject fraud fingerprinting scripts (for processors that support device fingerprinting)
          if (session?.fraud_script_urls?.length) {
            const loadPromises: Promise<void>[] = [];

            session.fraud_script_urls.forEach((url: string) => {
              const scriptId = getFraudScriptId(url);

              if (document.getElementById(scriptId)) {
                console.log(`[PaymentKit] Fraud script already present: ${scriptId}`);
                loadPromises.push(Promise.resolve());
                return;
              }

              const script = document.createElement("script");
              script.type = "text/javascript";
              script.async = true;
              script.id = scriptId;
              script.src = url;
              script.dataset.orderSessionId = checkoutRequestId;

              const loadPromise = new Promise<void>((resolve) => {
                script.onload = () => {
                  console.log(`[PaymentKit] Fraud script loaded: ${scriptId}`);
                  resolve();
                };
                script.onerror = () => {
                  console.error(
                    `[PaymentKit] Failed to load fraud prevention script. If using Content-Security-Policy, add to script-src: ${new URL(url).origin}`,
                  );
                  resolve(); // Resolve anyway — don't block payment on optional fraud script
                };
              });

              loadPromises.push(loadPromise);
              paymentKitStates._fraudScriptIds.push(scriptId);
              appendScriptToDOM(script);
            });

            paymentKitStates._fraudScriptsReady = Promise.all(loadPromises).then(() => {
              console.log(`[PaymentKit] All ${loadPromises.length} fraud fingerprinting script(s) ready`);
            });

            // Log each injected script with its data-order-session-id for debugging
            session.fraud_script_urls.forEach((url: string) => {
              const scriptId = getFraudScriptId(url);
              console.log(
                `[PaymentKit] Fraud script injected: id=${scriptId}, data-order-session-id=${checkoutRequestId}, src=${url}`,
              );
            });
            console.log(`[PaymentKit] Injected ${session.fraud_script_urls.length} fraud fingerprinting script(s)`);
          }
        } else {
          console.log("[PaymentKit] Session response missing card_tokenization_mode — defaulting to direct mode");
          paymentKitStates.cardTokenizationMode = "direct";
        }
      })
      .catch((err) => {
        console.error("[PaymentKit] Session config fetch error — falling back to direct mode:", err);
        paymentKitStates.cardTokenizationMode = "direct";
      });
  } else {
    console.log(`[PaymentKit] cardTokenizationMode explicitly set: ${cardTokenizationMode}`);
  }

  const pmInstances = paymentMethods.map((paymentMethod) => paymentMethod(paymentKitStates));

  const externalFuncsMapByPm: ExternalFuncsMapByPm<PaymentMethods> = pmInstances.reduce(
    (acc, { name, externalFuncs }) => {
      // @ts-expect-error - typecase this better in future
      acc[name] = externalFuncs;
      return acc;
    },
    {} as ExternalFuncsMapByPm<PaymentMethods>,
  );

  const submit: PaymentKitReturnType["submit"] = ({
    paymentMethod: paymentMethodName,
    fields,
    options,
    onSuccess,
    onError,
  }) => {
    const paymentMethod = pmInstances.find(({ name }) => name === paymentMethodName);
    if (!paymentMethod) {
      onError({ root: "payment_method_not_found" });
      return;
    }
    paymentMethod.internalFuncs
      .submitPayment(fields, options)
      .then(({ data, errors }) => {
        errors ? onError(errors) : onSuccess(data);
      })
      .catch((e) => {
        console.error("PaymentKit:submit:catch", e);

        // Try to extract error message from response
        if (e?.response?.data) {
          onError(e.response.data);
        } else if (e?.message) {
          onError({ root: e.message });
        } else {
          onError({ root: "unknown_error" });
        }
      });
  };

  const cleanup = () => {
    // Clean up fraud fingerprinting scripts
    for (const scriptId of paymentKitStates._fraudScriptIds) {
      const script = document.getElementById(scriptId);
      if (script) {
        script.remove();
      }
    }
    paymentKitStates._fraudScriptIds = [];

    // Clean up all payment method instances
    for (const pm of pmInstances) {
      if (pm.internalFuncs.cleanup) {
        pm.internalFuncs.cleanup();
      }
    }
    unmountTunnelX();
  };

  return {
    submit,
    cleanup,
    ...externalFuncsMapByPm,
  };
};

export type { PaymentKitEnvironment, PaymentKitFields } from "./types";

export default PaymentKit;
